<template>
	<v-container
		class="brightBackground"
		fluid
		style="min-height: 100%"
		width="100%"
	>
		
		<v-container fluid style="max-width: 1100px;" class="pt-xs-0 pt-sm-0 pt-md-0 pt-lg-6">

			<v-row>
				<v-col class="pt-0 pa-0 ma-0">
					<v-card v-if="tabItems.length > 0" class="mb-3">

						<!-- -------------------------------------------- -->
						<!--   Tab menu -->
						<!-- -------------------------------------------- -->
						<v-tabs
							v-model="tabActive"
							background-color="secondary accent-4"
							center-active
							dark
							show-arrows
						>

							<!-- Tab button -->
							<v-tab
								v-for="item in tabItems"
								:key="item.id"
								style="text-transform: none;"
							>
								<div class="mr-1">{{ (item.name.length > 25) ? item.name.substring(0, 23)+'...' : item.name  }}</div>

								<!-- Tab close button -->
								<v-btn
									class="ml-1 mr-n2"
									right
									icon
									x-small
									@click.stop="removeTabItemAndListenerById(item.id)"
									color="grey lighten-1"
								>
									<v-icon>mdi-close</v-icon>
								</v-btn>
							</v-tab>
							
						</v-tabs>
					
					</v-card>


					<!-- -------------------------------------------- -->
					<!--   Tab content container -->
					<!-- -------------------------------------------- -->

					<!-- I am NOT using Vuetifys tab-items, they have very buggy UI behaviour when opening new tabs. Better just use v-cards and v-show="" -->
					<v-card v-for="item in tabItems" :key="item.id"
						v-show="(tabActiveId === item.id)"
						color="boxOnBrightBackground"
						flat
						class="pa-3 mb-4"
					>
						<!-- Tab component - Either a simple subfolder edit, or a bigger View Template Editor -->
						<keep-alive>
							<component :dio="item.documentIO" :is="item.component" />
						</keep-alive>
					</v-card>
					

					
					
				</v-col>
			</v-row>
			
		</v-container>

	</v-container>
</template>



<script>

// ----------------------------------------------------
//
//  Main script
//
// ----------------------------------------------------

import Vue from 'vue';
import agile from '@/shift/agile';
import fb from '@/shift/fb';
import { AiEventBus } from '@/main';


export default
{
	name: 'ContentEditorTabMenu',
	
	
	// ----------------------------------
	//	VUE: Data and computed
	// ----------------------------------

	data: () =>
	({
		tabActive: null,
		tabItems: [],

		documentIoCollection: [],
		templateCollection: [],

		initialSyncUserTab: null,
		waitingForInitialSyncToRest: true,
		hasInitializedUserSync: false,
		allowUserSync: true,
	}),

	computed:
	{
		agile()
		{
			return agile;
		},

		routeKey()
		{
			return this.$route.params.key;
		},

		tabActiveId()
		{
			if (this.tabItems[this.tabActive])
			{
				return this.tabItems[this.tabActive].id;
			}
			else
			{
				return this.tabItems[0].id;
			}
			
		}
	},


	// ----------------------------------
	//	VUE: Watching things
	// ----------------------------------

	watch:
	{
		// -------------------------------------------------------
		//
		//  Send user state of open and active tabs to firestore
		//
		// -------------------------------------------------------

		tabActive()
		{
			this.syncronizeTabSettingsToServer();
		}
	},



	// ----------------------------------------------------
	//
	//  VUE: Create and Mount
	//
	// ----------------------------------------------------

	created()
	{
		AiEventBus.$on('contentEditorOpenFolder', this.openSortingFolderEditorWithLiveListener);
		AiEventBus.$on('contentEditorOpenDocument', this.openContentEditorWithLiveListener);
	},

	mounted()
	{
		const scopeThis = this;

		// This is to give the connection a chance to set the active tab to the users last active tab
		// The code for this is far from fool proof, but on fast connections, the page should usually land in the last document he edited.
		setTimeout(function()
		{
			scopeThis.waitingForInitialSyncToRest = false;
		}, 2000);
		
	},


	// --------------------------------------------------------------------
	//
	//	VUE: Methods
	//
	// --------------------------------------------------------------------

	methods:
	{

		syncronizeTabSettingsToServer()
		{
			// We want to save which documents we have open in the tab menu, and the active tab
			if (agile.loggedInUser.uid && this.hasInitializedUserSync && this.allowUserSync)
			{
				
				// We don't want to spam the firestore database, wait atleast 1 second before we update, and block other requests to sync meanwhile
				this.allowUserSync = false;
				
				setTimeout(() =>
				{
					this.allowUserSync = true;

					// Create array with open tabs id's
					const openTabs = [];
					for (let i = 0; i < this.tabItems.length; i++)
					{
						openTabs.push( { id: this.tabItems[i].id, itemType: this.tabItems[i].itemType } );
					}
					const activeTabId = (this.tabItems.length > 0) ? this.tabItems[this.tabActive].id : null ;

					const saveStateData =
					{
						contentEditorActiveTab: activeTabId,
						contentEditorOpenTabs: openTabs
					};
					
					// Ok, do the sync command
					fb.db.collection("users").doc(agile.loggedInUser.uid).collection("saved-states").doc(this.routeKey).set(saveStateData, { merge: true })
					.then(() =>
					{
						//console.log("Updated user tab states", this.treeOpenNodes);
					})
					.catch((error) =>
					{
						console.log('Error updating active tabs state:', error.message);
					});

				}, 1000);

				
			}
		},



		// --------------------------------------------------------------------
		//
		//	Sync tabs from when the user last worked (called from ViewsEditor.vue)
		//
		// --------------------------------------------------------------------

		syncInitialTabs(activeTab, openTabs)
		{
			if (openTabs && openTabs.length > 0)
			{
				for (let i = 0; i < openTabs.length; i++)
				{
					if (openTabs[i].itemType === 'sortingFolder')
					{
						this.openSortingFolderEditorWithLiveListener(openTabs[i].id);
					}
					else if (openTabs[i].itemType === 'content')
					{
						this.openContentEditorWithLiveListener(openTabs[i].id);
					}
				}

				this.initialSyncUserTab = activeTab;
				
			}
		},


		
		// --------------------------------------------------------------------
		//
		//	Open a sorting folder document
		//
		// --------------------------------------------------------------------

		openSortingFolderEditorWithLiveListener(id)
		{
			for (let i = 0; i < this.tabItems.length; i++)
			{
				if (this.tabItems[i].id == id)
				{
					// Tab is already open, focus on it
					const tabIndex = agile.getArrayIndexOfSub(this.tabItems, 'id', id);
					if (tabIndex > -1) this.tabActive = tabIndex;

					// Stop further progress in this function
					return;
				}
			}
			
			const itemToOpen = agile.getObjectById(this.$parent.$parent.$refs.sharedTreeComponent.treeItems, id);

			const documentIO = Vue.observable(
			{
				id: id,
				contentType: 'folder',
				garbage: false,
				wrapper:
				{
					initialized: true,
					doc: itemToOpen.doc,
					docData: itemToOpen,
				},
			});

			this.documentIoCollection.push(documentIO);

			this.tabItems.push(
			{
				id: documentIO.id,
				name: documentIO.wrapper.docData.editorMetaData.treeNodeLabel,
				itemType: 'sortingFolder',
				component: 'content-edit-folder',
				documentIO: documentIO,
			});


			if (this.waitingForInitialSyncToRest)
			{
				if (this.initialSyncUserTab === documentIO.id) this.tabActive = this.tabItems.length-1;
			}
			else
			{
				this.tabActive = this.tabItems.length-1;
			}

		},


		// ----------------------------------
		//	Update wrapper with new live data
		// ----------------------------------

		liveUpdateWrapper(docData)
		{
			for (let i = 0; i < this.tabItems.length; i++)
			{
				if (this.tabItems[i].id === docData.id)
				{
					this.tabItems[i].name = docData.editorMetaData.treeNodeLabel;
					this.tabItems[i].documentIO.wrapper.docData = docData;
				}
			}
		},


		// --------------------------------------------------------------------
		//
		//	Open View template
		//
		// --------------------------------------------------------------------
		
		openContentEditorWithLiveListener(id)
		{
			for (let i = 0; i < this.documentIoCollection.length; i++)
			{
				if (this.documentIoCollection[i].id == id)
				{
					// Tab is already open, focus on it
					const tabIndex = agile.getArrayIndexOfSub(this.tabItems, 'id', id);
					if (tabIndex > -1) this.tabActive = tabIndex;
					
					// Stop further progress in this function
					return;
				}
			}


			// Try to find the page we want to edit in the content tree
			let itemToOpen = agile.getObjectById(this.$parent.$parent.$refs.treeComponent.treeItems, id);
			// If not, try the shared content tree
			if (!itemToOpen) itemToOpen = agile.getObjectById(this.$parent.$parent.$refs.sharedTreeComponent.treeItems, id);

			const actualId = (itemToOpen.originalId) ? itemToOpen.originalId : itemToOpen.id ;
			
			const documentIO = Vue.observable(
			{
				id: itemToOpen.id,
				contentType: itemToOpen.nodeType,
				garbage: false,
				wrapper:
				{
					actualId: actualId,
					initialized: true,
					doc: itemToOpen.doc,
					docData: itemToOpen,
				},
				real:
				{
					actualId: actualId,
					initialized: false,
					removeRealTimelistener: null,
					doc: null,
					docData: null,
				},
				template:
				{
					id: itemToOpen.editorMetaData.templateId,
					name: itemToOpen.editorMetaData.templateName,
					link: null,
				}
			});

			
			// --------------------------------------------------------------------
			//
			//	Firestore real doc
			//
			// --------------------------------------------------------------------
			
			let docId = itemToOpen.id;
			let db = 'content';

			if (itemToOpen.nodeType === 'shared-content')
			{
				db = 'shared-content';

				// Original ID, it means that if this property is set, the content is
				// an instance of shared object in the content tree. Hence we need to get
				// the original object, the source for the  shared instance.
				if (itemToOpen.originalId) docId = itemToOpen.originalId;
			}
						
			const realDoc = fb.db
				.collection('content-collection')
				.doc('content-doc')
				.collection(this.routeKey)
				.doc(this.routeKey)
				.collection(db)
				.doc(docId)
				.collection('document')
				.doc('content');

			

			// Create firestore listener for real document
			documentIO.real.removeRealTimelistener = realDoc.onSnapshot(docSnapshot =>
			{
				if (!documentIO.real.initialized)
				{
					// ------------------------------------------------
					//	Open the document and keep listening
					// ------------------------------------------------

					if (docSnapshot.exists)
					{
						documentIO.real.initialized = true;
						documentIO.real.doc = docSnapshot;
						documentIO.real.docData = docSnapshot.data();
						this.tryOpenTab(documentIO);
					}
					else
					{
						// Query went to document that does not exist. Shut down.
						console.log('* Opening "real" document, but it does not exist :(');
						this.removeDocumentIoById(documentIO.id);
					}
				}
				else
				{
					// ------------------------------------------------
					//	Modify the document
					// ------------------------------------------------
					
					let wasTrashedOrDeleted = false;

					if (docSnapshot.exists)
					{
						documentIO.real.doc = docSnapshot;
						documentIO.real.docData = docSnapshot.data();
						wasTrashedOrDeleted = documentIO.real.docData.trash;
					}
					else
					{
						// If document suddenly does not exist any more, then it must have been deleted, i.e. by another user
						// Remove listener and remove from tab menu
						wasTrashedOrDeleted = true;

						// this.$root.ai_app.snackbarText = `'${documentIO.real.docData.name}' was just deleted by someone else`;
						// this.$root.ai_app.snackbarTimeout = 8000;
						// this.$root.ai_app.snackbar = true;
					}

					if (wasTrashedOrDeleted)
					{
						this.removeTabItemAndListenerById(id);
					}
				}
			},
			er =>
			{
				this.removeDocumentIoById(documentIO.id);
				this.$root.$alert("Error", er.message);
			});


			this.documentIoCollection.push(documentIO);
			this.addTemplateListener(documentIO);
			
		},


		addTemplateListener(documentIO)
		{
			
			for (let i = 0; i < this.templateCollection.length; i++)
			{
				if (this.templateCollection[i].id === documentIO.template.id)
				{
					// Link the objects together
					documentIO.template.link = this.templateCollection[i];
					this.templateCollection[i].linked.push(documentIO);

					// ------------------------------------------------
					//	If we found existing template live listener
					//	then we are happy and just return.
					// ------------------------------------------------

					return;
				}
			}

			// If we didnt find existing template listener, then we set it up

			const template =
			{
				name: documentIO.template.name,
				id: documentIO.template.id,
				initialized: false,
				removeRealTimelistener: null,
				doc: null,
				docData: null,
				linked: [],
				garbage: false,
			}

			// --------------------------------------------------------------------
			//
			//	Firestore template doc
			//
			// --------------------------------------------------------------------
			
			const templateDoc = fb.db
				.collection('dev-collection')
				.doc('dev-doc')
				.collection(this.routeKey)
				.doc(this.routeKey)
				.collection('view-templates')
				.doc(template.id);
			
			
			// Create firestore listener for shell document
			template.removeRealTimelistener = templateDoc.onSnapshot(docSnapshot =>
			{
				if (!template.initialized)
				{
					if (docSnapshot.exists)
					{
						template.initialized = true;
						template.doc = docSnapshot;
						template.docData = docSnapshot.data();
						//this.checkForTabsToOpen(template);
						this.linkTemplateTodocs(template);
					}
					else
					{
						// Query went to document that does not exist. Shut down.
						console.log(`* Tried to open template ${template.name}, but it doesn't exist`);
						this.removeTemplateListenerById(template.id);
					}
				}
				else
				{
					// ------------------------------------------------
					//	Modify the document
					// ------------------------------------------------

					if (docSnapshot.exists)
					{
						template.docData = docSnapshot.data();
						//original.name = docData.name;
					}
					else
					{
						// If document does not exist any more, then it must have been deleted
						// Remove listener and remove from tab menu

						this.removeDocumentsWithTemplateId(template.id);
						this.removeTemplateListenerById(template.id);
						
						this.$root.ai_app.snackbarText = `Template '${template.name}' was just deleted by someone`;
						this.$root.ai_app.snackbarTimeout = 5000;
						this.$root.ai_app.snackbar = true;

						// kill pages that are member of this then!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
						console.log('* Seems like someone deleted the template.');
					}

				}
				
			},
			er =>
			{
				this.removeTemplateListenerById(template.id);
				this.$root.$alert("Error template listener", er.message);
			});


			// ------------------------------------------------
			//	Add template listener to collection
			// ------------------------------------------------

			this.templateCollection.push(template);

		},


		linkTemplateTodocs(template)
		{
			for (let i = 0; i < this.documentIoCollection.length; i++)
			{
				const docIO = this.documentIoCollection[i];

				if (docIO.contentType !== 'folder' && !docIO.template.link && docIO.template.id === template.id)
				{
					docIO.template.link = template;
					template.linked.push(docIO)
					
					// Check if document are waiting to be opened in the tabs
					this.tryOpenTab(docIO);
				}
			}
		},
		

		tryOpenTab(documentIO)
		{
			// If template is not set yet, abort
			if (!documentIO.template.link || !documentIO.real.initialized) return;

			if (documentIO.real.initialized && documentIO.template.link.initialized)
			{
				this.tabItems.push(
				{
					id: documentIO.id,
					name: documentIO.real.docData.name,
					itemType: 'content',
					component: 'content-edit-page',
					documentIO: documentIO,
				});

				if (this.waitingForInitialSyncToRest)
				{
					if (this.initialSyncUserTab === documentIO.id) this.tabActive = this.tabItems.length-1;
				}
				else
				{
					this.tabActive = this.tabItems.length-1;
				}
			}
		},


		clearUnusedTemplateConnections()
		{
			for (let i = 0; i < this.templateCollection.length; i++)
			{
				const template = this.templateCollection[i];
				
				for (let j = template.linked.length-1; j >= 0; j--)
				{
					if (template.linked[j].garbage) template.linked.splice(j, 1);
				}

				if (template.linked.length <= 0) this.removeTemplateListener(template);
			}
		},



		// --------------------------------------------------------------------
		//
		//	Utility functions
		//
		// --------------------------------------------------------------------

		removeDocumentsWithTemplateId(id)
		{
			for (let i = this.documentIoCollection.length-1; i >= 0; i--)
			{
				if (this.documentIoCollection[i].template.id == id) this.removeDocumentIoById(this.documentIoCollection[i].id);
			}
		},
		
		// ----------------------------------------------------
		//	Get/remove tabs
		// ----------------------------------------------------

		getTabItemById(id)
		{
			for (let i = 0; i < this.tabItems.length; i++)
			{
				if (this.tabItems[i].id === id)
				{
					return this.tabItems[i];
				}
			}
			return null;
		},

		removeTabItemAndListenerById(id)
		{
			this.removeTabItemById(id);
			this.removeDocumentIoById(id);
		},

		removeTabItemById(id)
		{
			for (let i = 0; i < this.tabItems.length; i++)
			{
				if (this.tabItems[i].id === id)
				{
					this.tabItems.splice(i, 1);
					
					if (this.tabItems.length < 1) this.syncronizeTabSettingsToServer();
					return;
				}
			}
			
			
		},


		// ----------------------------------------------------
		//	Get/remove document IO connections
		// ----------------------------------------------------

		getDocumentIoById(id)
		{
			for (let i = 0; i < this.documentIoCollection.length; i++)
			{
				if (this.documentIoCollection[i].id === id) return this.documentIoCollection[i];
			}
		},

		removeDocumentIoById(id)
		{
			for (let i = 0; i < this.documentIoCollection.length; i++)
			{
				if (this.documentIoCollection[i].id === id)
				{
					this.removeDocumentIoListener(this.documentIoCollection[i]);
					break;
				}
			}
		},

		removeDocumentIoListener(documentIO)
		{
			documentIO.garbage = true;
			if (documentIO.real) documentIO.real.removeRealTimelistener();
			agile.removeObjectFromArrayBasedOnSubObjectMatch(this.documentIoCollection, 'id', documentIO.id);
			this.clearUnusedTemplateConnections();
		},


		// ----------------------------------------------------
		//	Get/remove template connections
		// ----------------------------------------------------

		removeTemplateListenerById(id)
		{
			for (let i = 0; i < this.templateCollection.length; i++)
			{
				if (this.templateCollection[i].id === id)
				{
					this.removeTemplateListener(this.templateCollection[i]);
					break;
				}
			}
		},

		removeTemplateListener(template)
		{
			template.garbage = true;
			template.removeRealTimelistener();
			agile.removeObjectFromArrayBasedOnSubObjectMatch(this.templateCollection, 'id', template.id);
		},

		
	},


	// --------------------------------------------------------------------
	//
	//	VUE: Destroy event hook
	//
	// --------------------------------------------------------------------

	beforeDestroy()
	{
		// Close down live listeners
		//console.log("* Unsubscribing live listeners for View templates editing opended");
		
		for (let i = this.documentIoCollection.length-1; i >= 0; i--)
		{
			this.removeDocumentIoListener(this.documentIoCollection[i]);
		}
		
		for (let i = this.templateCollection.length-1; i >= 0; i--)
		{
			this.removeTemplateListener(this.templateCollection[i]);
		}

		AiEventBus.$off('contentEditorOpenFolder', this.openSortingFolderEditorWithLiveListener);
		AiEventBus.$off('contentEditorOpenDocument', this.openContentEditorWithLiveListener);
		
	},
    
}
</script>